// SPDX-License-Identifier: AGPL-3.0-or-later
// KONVO: the Font section of Appearance. Three stacks: the platform's own UI
// font (the default), the bundled Konvo face, and a serif for people who read
// better with one.

import FontPreference, {type InterfaceFont, InterfaceFonts} from '@app/features/theme/state/FontPreference';
import type {RadioOption} from '@app/features/ui/radio_group/RadioGroup';
import {RadioGroup} from '@app/features/ui/radio_group/RadioGroup';
import type {I18n} from '@lingui/core';
import {msg} from '@lingui/core/macro';
import {useLingui} from '@lingui/react/macro';
import {observer} from 'mobx-react-lite';

const KONVO_LABEL = msg({message: 'Konvo', comment: "Font choice: the app's bundled typeface (Fluxer Sans)."});
const KONVO_DESCRIPTION = msg({
	message: 'The bundled Konvo typeface, the same on every device.',
	comment: 'Description under the Konvo font choice.',
});
const SYSTEM_LABEL = msg({message: 'System', comment: "Font choice: the operating system's interface font."});
const SYSTEM_DESCRIPTION = msg({
	message: 'Your device’s own interface font, like San Francisco on iPhone or Segoe on Windows. The default.',
	comment: 'Description under the System font choice.',
});
const SERIF_LABEL = msg({message: 'Serif', comment: 'Font choice: a serif typeface.'});
const SERIF_DESCRIPTION = msg({
	message: 'A book-style serif for longer reading.',
	comment: 'Description under the Serif font choice.',
});

function getFontOptions(i18n: I18n): Array<RadioOption<InterfaceFont>> {
	return [
		{value: InterfaceFonts.SYSTEM, name: i18n._(SYSTEM_LABEL), desc: i18n._(SYSTEM_DESCRIPTION)},
		{value: InterfaceFonts.KONVO, name: i18n._(KONVO_LABEL), desc: i18n._(KONVO_DESCRIPTION)},
		{value: InterfaceFonts.SERIF, name: i18n._(SERIF_LABEL), desc: i18n._(SERIF_DESCRIPTION)},
	];
}

export const FontTabContent = observer(() => {
	const {i18n} = useLingui();
	return (
		<RadioGroup
			options={getFontOptions(i18n)}
			value={FontPreference.font}
			onChange={(value) => FontPreference.setFont(value)}
			data-flx="user.appearance-tab.font-tab.radio-group"
		/>
	);
});
